import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import createFetchData from 'dpl/shared/fetching/utils/createFetchData';
import generateQueryKey from 'dpl/shared/fetching/utils/generateQueryKey';

import { breederAvailabilityResourceDefinition } from '../utils/queryDefinitions';

// Fetch a breeder's availability through the react-query cache, outside the
// React render cycle. Shares the query key with useBreederAvailability, so
// whichever side asks first does the network work and the other reads it
// back for free: already-cached → resolves instantly; in flight → awaits the
// same request; missing → fetches and leaves the cache warm for the card.
export function fetchBreederAvailability(
  queryClient,
  breedId,
  breederProfileId
) {
  const { url, params, defaultValue } = breederAvailabilityResourceDefinition(
    breedId,
    breederProfileId
  );

  return queryClient
    .fetchQuery(generateQueryKey({ params, url }), () =>
      createFetchData({ url, params })
    )
    .then(({ data }) => data || defaultValue)
    .catch(() => defaultValue);
}

export default function useBreederAvailability({
  breedId,
  breederProfileId,
  enabled
}) {
  const {
    value: {
      availability_description: availabilityDescription,
      available_puppy_count: availablePuppyCount,
      available_adult_dogs_count: availableAdultDogsCount,
      analytics_availability_info: analyticsAvailabilityInfo,
      formatted_upcoming_litters_due_dates: formattedUpcomingLittersDueDates
    },
    isSuccess
  } = useResourceQuery({
    ...breederAvailabilityResourceDefinition(breedId, breederProfileId),
    enabled: Boolean(enabled && breedId && breederProfileId)
  });

  return {
    availabilityDescription: availabilityDescription || null,
    availablePuppyCount: availablePuppyCount || 0,
    availableAdultDogsCount: availableAdultDogsCount || 0,
    analyticsAvailabilityInfo: analyticsAvailabilityInfo || null,
    formattedUpcomingLittersDueDates: formattedUpcomingLittersDueDates || [],
    isLoaded: isSuccess
  };
}
